import { http } from '@/network/http';
import type {
  UserListItem,
  UserDetail,
  CreateUserData,
  UpdateUserData,
  UpdateProfileData,
  PendingUser,
} from './types';

export const usersApi = {
  list: (params?: Record<string, unknown>) =>
    http.get<{
      success: boolean;
      users: UserListItem[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/users', { params }),

  getById: (id: string) =>
    http.get<{ success: boolean; user: UserDetail }>(`/users/${id}`),

  create: (data: CreateUserData) =>
    http.post<{ success: boolean; user: UserListItem }>('/users', data),

  update: (id: string, data: UpdateUserData) =>
    http.put<{ success: boolean; user: UserListItem }>(`/users/${id}`, data),

  changeRole: (id: string, role: string) =>
    http.patch<{ success: boolean; user: UserListItem }>(`/users/${id}/role`, { role }),

  activate: (id: string, active: boolean) =>
    http.patch<{ success: boolean; user: UserListItem }>(`/users/${id}/activate`, { active }),

  updateHospitals: (id: string, hospitalIds: string[], primaryHospitalId?: string) =>
    http.put(`/users/${id}/hospitals`, { hospitalIds, primaryHospitalId }),

  getTherapists: () =>
    http.get<{ success: boolean; therapists: Array<{ id: string; firstName: string | null; lastName: string | null; email: string }> }>('/users/therapists'),

  getPending: () =>
    http.get<{ success: boolean; users: PendingUser[] }>('/users/pending'),

  approve: (id: string, body?: { employmentRoleId?: string; hospitalIds?: string[]; primaryHospitalId?: string }) =>
    http.post(`/users/${id}/approve`, body),

  reject: (id: string) =>
    http.post<{ success: boolean }>(`/users/${id}/reject`),

  updateProfile: (data: UpdateProfileData) =>
    http.put<{ success: boolean; user: UserListItem }>('/users/me/profile', data),

  getMyHospitals: () =>
    http.get<{
      success: boolean;
      hospitals: Array<{ id: string; name: string; nameEn?: string | null; code: string; city: string; cityEn?: string | null; isPrimary: boolean }>;
    }>('/users/me/hospitals'),

  setPrimaryHospital: (hospitalId: string) =>
    http.put<{
      success: boolean;
      hospitals: Array<{ id: string; name: string; nameEn?: string | null; code: string; city: string; cityEn?: string | null; isPrimary: boolean }>;
    }>('/users/me/primary-hospital', { hospitalId }),

  addMyHospitals: (hospitalIds: string[]) =>
    http.post<{
      success: boolean;
      hospitals: Array<{ id: string; name: string; nameEn?: string | null; code: string; city: string; cityEn?: string | null; isPrimary: boolean }>;
    }>('/users/me/hospitals', { hospitalIds }),

  removeMyHospital: (hospitalId: string) =>
    http.delete<{
      success: boolean;
      hospitals: Array<{ id: string; name: string; nameEn?: string | null; code: string; city: string; cityEn?: string | null; isPrimary: boolean }>;
    }>(`/users/me/hospitals/${hospitalId}`),

  getRelatedRecords: (id: string) =>
    http.get<{ success: boolean; reports: number; payrollEntries: number; payrollRuns: number }>(`/users/${id}/related-records`),

  delete: (id: string) =>
    http.delete<{ success: boolean }>(`/users/${id}`),
};
